import type { Entry } from 'contentful'
/* eslint-disable react/jsx-props-no-spreading */
import Link from '@/components/link-component'
import { cx } from '@/utils/strings'
import type { TypeComponentIconSkeleton } from '@/types/ctf'

export const buttonStyles = {
  btnArrow: 'Arrow Link',
  btnOutlined: 'Button: Outlined',
  btnSolid: 'Button: Solid',
  btnPlain: 'Plain Link',
  btnText: 'Text Link',
}

const { btnSolid, btnOutlined, btnArrow, btnPlain, btnText } = buttonStyles

export type DisplayType = 'Inline' | 'Block'
export type AlignType = 'Left' | 'Center'
export type SizeType = 'Base' | 'Larger'
export type VariantType = typeof btnArrow | typeof btnOutlined | typeof btnSolid | typeof btnPlain | typeof btnText

export type CtaLinkProps = {
  align?: AlignType // Used in component logic
  asButton?: boolean // Used to determine rendering
  bgGradient?: boolean // Used in styling logic
  bold?: boolean // Used in styling logic
  children?: any // Used to render content
  className?: string // Used for styling
  css?: string // Used in buttonStyle
  disabled?: boolean // Disabled button style
  display?: DisplayType // Used in styling
  icon?: Entry<TypeComponentIconSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> // Used for icon rendering
  iconPosition?: string // Used for icon positioning
  internalName?: string // Default provided but not directly used in rendering
  onClick?: React.MouseEventHandler<HTMLAnchorElement> // Would be spread in ...other
  plausibleEventName?: string // Used for analytics class
  scrollToOffset?: number // Would be spread in ...other
  size?: SizeType // Used for styling
  style?: VariantType // Used for styling variations
  to?: string // Used for link destination
  [key: string]: any // Catch-all for other props
}

export default function CtaLink({
  align = 'Left',
  asButton = false,
  bgGradient,
  bold = false,
  children,
  css,
  disabled,
  display = 'Inline',
  icon = undefined,
  iconPosition = 'Left',
  internalName = 'no name',
  className: _className,
  plausibleEventName,
  size = 'Base',
  style = btnArrow,
  to,
  ...other
}: CtaLinkProps): JSX.Element {
  // remove props we don't want to pass to the button or link
  delete other?.link

  /**
   * Focus styles for the button
   *
   * Note: Specify the bg & border color with `focus:before:` where you apply this
   */
  const btnFocusStyle = [
    'before:content-[""]',
    'before:absolute',
    'before:top-0',
    'before:left-0',
    'before:w-full',
    'before:h-full',
    'before:rounded-full',
    'before:transition-colors',
    'focus:before:z-10',
    'focus:outline-hidden',
    'focus:before:border-2',
    'focus:before:border-dashed',
    'not-disabled:before:animate-button-shrink',
    'not-disabled:hover:before:animate-button-grow',
  ].join(' ')

  const textFocusStyle =
    'focus:outline-hidden focus:border-b focus:border-secondary-700 dark:focus:border-primaryBlue hover:[&_.arrow]:translate-x-1 group-hover:[&_.arrow]:translate-x-1'

  let className = _className || ''
  if (plausibleEventName?.trim()) {
    className += ` ${plausibleEventName}`
  }

  const buttonStyle = cx(
    'font-medium z-0',
    size !== 'Larger' && 'text-base',
    size === 'Larger' && 'text-lg',
    align === 'Center' && 'mx-auto',
    display === 'Inline' && 'inline-block',
    display === 'Block' && 'table',
    !disabled &&
      (btnArrow === style || btnPlain === style) &&
      !bgGradient &&
      `border-b-1 border-b-transparent text-primaryBlue hover:text-primary-700 dark:hover:text-primaryBlue ${textFocusStyle}`,
    !disabled &&
      (btnArrow === style || btnPlain === style) &&
      bgGradient &&
      `border-b-1 border-b-transparent text-white hover:text-primary-100 dark:hover:text-primaryBlue ${textFocusStyle}`,
    (style === btnOutlined || style === btnSolid) && 'relative rounded-full text-center',
    size !== 'Larger' && (style === btnOutlined || style === btnSolid) && 'px-5 py-3',
    size === 'Larger' && (style === btnOutlined || style === btnSolid) && 'px-9 py-4',
    btnPlain === style && 'underline text-primaryBlue ',
    !disabled &&
      btnSolid === style &&
      !bgGradient &&
      `bg-primaryBlue text-white dark:text-secondary-800 cta-link-hover-no-bg ${btnFocusStyle} focus:before:border-white dark:focus:before:border-secondary-800`,
    btnOutlined === style && `${btnFocusStyle} before:border-2`,
    !disabled &&
      btnOutlined === style &&
      !bgGradient &&
      `text-primaryBlue cta-link-hover-no-bg ${btnFocusStyle} before:border-primaryBlue hover:before:border-white dark:hover:before:border-secondary-800 focus:before:border-white dark:focus:before:border-secondary-800`,
    !disabled &&
      style === btnOutlined &&
      bgGradient &&
      `text-white before:border-white hover:text-primary-500 hover:focus:before:border-primary-500 cta-link-hover after:bg-white`,
    !disabled &&
      style === btnSolid &&
      bgGradient &&
      `bg-white text-primary-500 before:border-white hover:text-primary-500 ${btnFocusStyle} focus:before:border-primary-500 cta-link-hover after:bg-primary-100`,
    btnText === style && 'no-underline',
    css,
    className.trim(),
    disabled && 'cursor-disabled focus:outline-hidden',
    disabled && (btnArrow === style || btnPlain === style) && !bgGradient && 'text-muted',
    disabled && (btnArrow === style || btnPlain === style) && bgGradient && 'text-[#A8B3C8]',
    disabled && !bgGradient && btnSolid === style && 'bg-secondary-300 dark:bg-secondary-600 text-muted',
    disabled &&
      !bgGradient &&
      btnOutlined === style &&
      'before:border-secondary-300 hover:before:border-secondary-300 text-muted',
    disabled && bgGradient && btnSolid === style && 'bg-[#A8B3C8] hover:bg-[#A8B3C8] text-muted-base',
    disabled && bgGradient && btnOutlined === style && 'before:border-[#A8B3C8] text-muted-base hover:bg-transparent'
  )

  const renderBtnText = () => (
    <span className={cx('flex items-center', bold && 'font-bold')}>
      {icon && iconPosition === 'Left' && (
        <span className="font-bwi mr-2 text-[16px]">{String.fromCodePoint(parseInt(icon.fields.unicode, 16))}</span>
      )}
      {children}
      {btnArrow === style && (
        <span className="arrow font-bwi ml-[6px] text-[14px] transition-transform duration-250 delay-75">&#xe91b;</span>
      )}
      {icon && iconPosition === 'Right' && (
        <span className="font-bwi ml-2 text-[16px]">{String.fromCodePoint(parseInt(icon.fields.unicode, 16))}</span>
      )}
    </span>
  )

  return asButton ? (
    <button
      disabled={disabled}
      type="button"
      aria-label={to && `go to ${to}`}
      data-testid="cta-link"
      className={buttonStyle}
      {...other}
    >
      {renderBtnText()}
    </button>
  ) : (
    <Link aria-label={`go to ${to}`} to={to} className={buttonStyle} data-testid="cta-link" {...other}>
      {renderBtnText()}
    </Link>
  )
}
